import {
	IconBus,
	IconDocument,
	IconDriver,
	IconPin,
	IconTime,
	IconTrip,
} from '@flixbus/honeycomb-icons-react';

import { BREAKDOWN_REASON_LABELS, REASONS_REQUIRING_ABSENCE } from 'utils/breakdown';
import { formatSummaryDateTime } from 'utils/datetime/fnsTimeFormatter';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';

const LABELS = CREATE_BREAKDOWN_FLOW_TEXT.summary.rowLabels;

/**
 * @typedef {import('../summarySectionsRegistry').SummaryRow} SummaryRow
 */

/**
 * Format a station-shaped object as `CODE (Name)`.
 *
 * @param {{ code?: string, name?: string } | null | undefined} station
 * @returns {string}
 */
const formatStation = (station) => {
	if (!station?.code) return '';
	if (!station.name) return station.code;
	return `${station.code} (${station.name})`;
};

/**
 * Format the breakdown location. Every entry is a real Station now
 * (X-stations come back from the `breakdownStations` query with `code` +
 * `name` set), so we can reuse the same formatter as `Segment`.
 *
 * @param {{ code?: string, name?: string } | null | undefined} loc
 * @returns {string}
 */
const formatBreakdownLocation = (loc) => {
	if (!loc) return '';
	return formatStation(loc);
};

/**
 * Format a segment as `CODE (Name) → CODE (Name)`.
 *
 * @param {{ fromStation?: object, toStation?: object } | null | undefined} segment
 * @returns {string}
 */
const formatSegment = (segment) => {
	if (!segment?.fromStation || !segment?.toStation) return '';
	return `${formatStation(segment.fromStation)} → ${formatStation(segment.toStation)}`;
};

const requiresAbsence = (reason) => REASONS_REQUIRING_ABSENCE.includes(reason);

/**
 * Build the row list for the Summary "Common" accordion.
 *
 * @param {object} data Flow-modal draft data.
 * @returns {SummaryRow[]}
 */
export const deriveCommonRows = (data) => {
	const rows = [
		{
			label: LABELS.breakdownReason,
			value: BREAKDOWN_REASON_LABELS[data.breakdownReason] ?? data.breakdownReason ?? '',
			icon: IconBus,
		},
		{
			label: LABELS.incidentTime,
			value: formatSummaryDateTime(data.incidentTimestamp),
			icon: IconTime,
		},
		{
			label: LABELS.segment,
			value: formatSegment(data.segment),
			icon: IconTrip,
		},
		{
			label: LABELS.breakdownLocation,
			value: formatBreakdownLocation(data.breakdownStation),
			icon: IconPin,
		},
	];

	if (requiresAbsence(data.breakdownReason) && data.originalDriverAbsence?.fromTimestamp) {
		rows.push({
			label: LABELS.originalDriverAbsenceStart,
			value: formatSummaryDateTime(data.originalDriverAbsence.fromTimestamp),
			icon: IconDriver,
		});
	}

	if (data.note) {
		rows.push({
			label: LABELS.note,
			value: data.note,
			icon: IconDocument,
		});
	}

	return rows;
};
